import { message } from 'antd';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import api from '../utils/api';
import { UserProps } from '@partsbadger/types';
import { clearAuthToken, refreshAuthToken, setAuthToken } from '@partsbadger/utils';

import QuoteStore from './QuoteStore';

export interface IGuestData {
    first_name: string;
    last_name: string;
    email: string;
    company: string;
}

class UserStore {
    loading = false;
    token: string | null = null;
    isAuthenticated = false;
    isGuest = false;
    user?: UserProps | null = null;
    first_initial?: string;
    errors: any = null;
    isVerified?: boolean | undefined;
    isStaff?: boolean | undefined;
    contact: any;
    guest_email: string | null = null;
    emailDomains: string[] | [] = [];
    estimatedTax = 0;

    guest_data: IGuestData | null = null;

    constructor() {
        window.localStorage.removeItem('token');
        this.guest_email = localStorage.getItem('guest_email') || null;

        this.isGuest = !!this.guest_email;

        const storedGuestData = localStorage.getItem('guest_data');

        if (storedGuestData) {
            try {
                this.guest_data = JSON.parse(storedGuestData);
            } catch (e) {
                localStorage.removeItem('guest_data');
                this.guest_data = null;
            }
        }

        makeObservable(this, {
            // Observables
            loading: observable,
            token: observable,
            isAuthenticated: observable,
            isGuest: observable,
            user: observable,
            first_initial: observable,
            errors: observable,
            isVerified: observable,
            isStaff: observable,
            contact: observable,
            guest_email: observable,
            estimatedTax: observable,
            guest_data: observable,

            // Actions
            setAuthenticated: action,
            setVerified: action,
            setIsStaff: action,
            setFirstInitial: action,
            setError: action,
            getEmail: action,
            getDefaultContact: action,
            getDefaultContactDetail: action,
            setGuestEmail: action,
            pullUser: action,
            restoreSession: action,
            logout: action,
            login: action,
            saveLead: action,
            checkEmailExists: action,
            register: action,
            restPasswordConfirm: action,
            changePassword: action,
            removeTokens: action,
            getTaxRateByZipCode: action,

            setGuestData: action,
            // Computed
            hasAContact: computed,
            defaultContactName: computed,
            defaultAccountName: computed,
        });
    }

    setGuestData(data: IGuestData | null) {
        this.guest_data = data;
        localStorage.setItem('guest_data', JSON.stringify(this.guest_data));
    }

    get hasAContact() {
        return this.user?.default_contact?.id;
    }
    get hasAccount() {
        return !!this.user?.default_contact?.account;
    }

    get defaultContactName() {
        return this.user?.default_contact?.fullname ?? null;
    }

    get defaultAccountName() {
        return this.user?.default_contact?.account ?? null;
    }

    setAuthenticated(bool: boolean) {
        this.isAuthenticated = bool;
    }

    setVerified(bool: boolean) {
        this.isVerified = bool;
    }

    setIsStaff(bool: boolean) {
        this.isStaff = bool;
    }

    setFirstInitial(initial: string) {
        this.first_initial = initial;
    }

    setError(err: any) {
        this.errors = err?.non_field_errors;
    }

    async getTaxRateByZipCode(zipCode: string) {
        const contactId = this.user?.default_contact?.id;
        if (contactId && zipCode) {
            return api.Contacts.getTaxRate(contactId, zipCode)
                .then(data => {
                    runInAction(() => {
                        this.estimatedTax = data?.estimated_combined_rate;
                    });
                    return this.estimatedTax;
                })
                .catch(err => {
                    return err;
                });
        }
        return 0;
    }

    getEmail() {
        if (this.isAuthenticated) {
            return this.user?.email;
        }
        return this.guest_email;
    }

    getDefaultContact() {
        return this.user?.default_contact?.id;
    }

    getDefaultContactDetail() {
        return this.user?.default_contact;
    }

    setGuestEmail(email = '') {
        window.localStorage.setItem('guest_email', email);
        this.guest_email = email;
    }

    pullUser() {
        this.loading = true;

        return api.User.get()
            .then(data => {
                runInAction(() => {
                    this.loading = false;
                    this.user = data;
                    this.isVerified = data?.is_verified_email || undefined;
                    this.isStaff = data?.is_staff || undefined;
                    this.isAuthenticated = true;
                    this.first_initial = data?.default_contact?.fullname?.charAt(0) || data?.first_name?.charAt(0);
                    this.contact = data?.default_contact;
                });

                return data;
            })
            .catch(error => {
                if (error?.response?.status == '401') {
                    runInAction(() => {
                        window.localStorage.removeItem('token');
                        clearAuthToken();
                        this.token = null;
                        this.isAuthenticated = false;
                    });
                }
            })
            .finally(() => {
                runInAction(() => {
                    this.loading = false;
                });
            });
    }

    async restoreSession() {
        try {
            const resp = await refreshAuthToken();

            runInAction(() => {
                this.token = resp?.token || null;
            });

            if (!resp?.token) {
                return false;
            }

            await this.pullUser();
            return true;
        } catch (_error) {
            runInAction(() => {
                this.token = null;
                this.isAuthenticated = false;
            });
            clearAuthToken();
            return false;
        }
    }

    removeTokens() {
        window.localStorage.removeItem('token');
        clearAuthToken();

        // Also remove quote token when the user logs out
        window.localStorage.removeItem('quote_token');

        // Remove QuickTurn order token from localStorage
        localStorage.removeItem('order_token');

        // Remove QuickTurn part token from localStorage
        localStorage.removeItem('part_token');

        runInAction(() => {
            this.token = null;
            this.isAuthenticated = false;
            this.isVerified = false;
            this.user = undefined;
            this.errors = null;
            this.setGuestEmail('');
            window.localStorage.removeItem('guest_email');
            window.location.href = '/';
        });
    }

    logout() {
        return api.User.logout()
            .then(() => {
                this.removeTokens();
            })
            .catch(error => {
                // The refresh cookie may still be valid, so the user must not look signed out.
                message.error('Sign-out did not complete. Please try again.');
                throw error;
            });
    }

    logoutAndClearSession() {
        return api.User.logout()
            .catch(error => {
                console.warn('Logout API call failed:', error);
            })
            .finally(() => {
                window.localStorage.removeItem('token');
                clearAuthToken();

                runInAction(() => {
                    this.token = null;
                    this.isAuthenticated = false;
                    this.isVerified = false;
                    this.user = null;
                    this.errors = null;
                });
            });
    }

    async associateQuote(quote_token: string) {
        if (quote_token) {
            return await QuoteStore.validateOwnerQuoteByUid(quote_token)
                .then(data => {
                    return data;
                })
                .catch(error => {
                    console.error('Error associating quote token with user:', error);
                    throw error;
                });
        }
    }

    login(email: string, password: string, onLoginSuccess?: () => void) {
        const data = { email: email, password: password };
        const quote_token = window.localStorage.getItem('quote_token');
        return api.User.login(data)
            .then(resp => {
                setAuthToken(resp['token']);
                this.setGuestData(null);
                runInAction(() => {
                    this.token = resp['token'];
                    this.pullUser();
                    this.isAuthenticated = true;
                    this.errors = null;
                });

                if (quote_token) {
                    // If we have a quote token, should request to backend to associate it with the user
                    return this.associateQuote(quote_token)
                        .then(res => {
                            // const quote_id = res?.quote_id || null;
                            window.localStorage.removeItem('quote_token');

                            return { quoteData: res, userData: resp?.user };
                            // window.location.replace('/my-account/quotes/' + Number(quote_id) + '/continue/');
                        })
                        .catch(error => {
                            throw error;
                        });
                }

                if (onLoginSuccess) {
                    onLoginSuccess();
                }

                return resp;
            })
            .catch(error => {
                runInAction(() => {
                    this.errors = error;
                });
                throw error;
            });
    }

    resend() {
        api.User.resend();
    }

    async resetPassword(email: string): Promise<void> {
        await api.User.resetPassword({ email });
    }

    saveLead(data: any) {
        return api.requests.post('/customer/leads/', data);
    }

    async checkEmailExists(email: string): Promise<boolean> {
        try {
            const res = await api.requests.get(`/customer/users/check-email/?email=${encodeURIComponent(email)}`);
            return Boolean(res?.exists);
        } catch (e) {
            // 429/5xx -> fall back to the existing flow (let /customer/quote/start/ run as before)
            return false;
        }
    }

    async register(data: any, onRegisterSuccess?: () => void) {
        const quote_token = window.localStorage.getItem('quote_token');
        return await api.User.register(data)
            .then(resp => {
                setAuthToken(resp['token']);
                window.localStorage.removeItem('setup_token');
                this.setGuestData(null);
                runInAction(() => {
                    this.token = resp['token'];
                    this.pullUser();
                    this.isAuthenticated = true;
                    this.errors = null;
                });
                if (quote_token) {
                    // If we have a quote token, should request to backend to associate it with the user
                    return this.associateQuote(quote_token)
                        .then(res => {
                            // const quote_id = res?.quote_id || null;
                            window.localStorage.removeItem('quote_token');

                            return { quoteData: res, userData: resp?.user };
                            // window.location.replace('/my-account/quotes/' + Number(quote_id) + '/continue/');
                        })
                        .catch(error => {
                            throw error;
                        });
                }

                if (onRegisterSuccess) {
                    onRegisterSuccess();
                }
                return resp;
            })
            .catch(error => {
                runInAction(() => {
                    this.loading = false;
                    this.errors = error;
                });

                throw error;
            });
    }

    getNotification(notificationId: number) {
        return api.requests.get(`/customer/notifications/${notificationId}/`);
    }

    restPasswordConfirm(data: any) {
        return api.User.restPasswordConfirm(data);
    }

    changePassword(data: any) {
        return api.User.changePassword(data);
    }

    referAFriend(data: any) {
        return api.User.referAFriend(data);
    }

    async requirements() {
        return await api.User.requirements();
    }

    requirementTypes() {
        return api.User.requirementTypes();
    }

    async updateRequirements(data: any) {
        return await api.User.updateRequirements(data);
    }

    defaultContact(id: number) {
        return api.User.defaultContact(id).then(() => {
            window.location.reload();
        });
    }

    getContactInvoice(id: number) {
        return api.User.getContactInvoice(id);
    }

    editContactInvoice(data: any, id: number) {
        return api.User.editContactInvoice(data, id);
    }

    getContact(id: number) {
        return api.User.getContact(id);
    }

    removeMobile(id: number) {
        return api.User.removeMobile(id);
    }

    getEmails() {
        return api.User.getEmails();
    }

    addEmail(data: any) {
        return api.User.addEmail(data);
    }

    removeEmail(id: number) {
        return api.User.removeEmail(id);
    }

    verifyOtherEmail(id: number) {
        return api.User.verifyEmail(id);
    }

    setPrimaryMail(id: number) {
        return api.User.setPrimary(id);
    }

    getAddresses() {
        return api.User.getAddresses();
    }

    addAddress(id: number, data: any) {
        return api.User.addAddress(id, data);
    }

    editAddress(data: any, id: number) {
        return api.User.editAddress(data, id);
    }

    removeAddress(id: number) {
        return api.User.removeAddress(id);
    }

    getDocuments() {
        return api.Documents.get();
    }

    removeDocument(id: number) {
        return api.Documents.remove(id);
    }

    getTaxExemptions() {
        return api.User.getTaxExemptions();
    }

    addTaxExemptions(data: any) {
        return api.User.addTaxExemption(data);
    }

    editTaxExemption(data: any, id: number) {
        return api.User.editTaxExemption(data, id);
    }

    removeTaxExemption(id: number) {
        return api.User.removeTaxExemption(id);
    }

    getNDAForm() {
        return api.User.getNDAForm();
    }

    addNDAForm(data: any) {
        return api.User.addNDAForm(data);
    }

    removeNDAForm(id: number) {
        return api.User.removeNDAForm(id);
    }

    editNDAForm(data: any, id: number) {
        return api.User.editNDAForm(data, id);
    }

    getEmailDomains() {
        return api.User.getEmailDomains()
            .then(res => {
                const domainList = res.results?.map((item: { id: number; domain: string }) =>
                    item?.domain?.toLowerCase()
                );
                runInAction(() => {
                    this.emailDomains = domainList || [];
                });
                return res;
            })
            .catch(err => {
                return err;
            });
    }
}
export default new UserStore();
